// B2B wholesale sales screen at /wholesale.
// Creates draft orders with orderType="wholesale" sourced from the wholesale
// workstation's warehouse (Kho Bán Buôn). Attaching a wholesale customer
// automatically keeps server-side wholesale pricing.
//
// Warehouse sourcing: admin picks the workstation via the WorkstationSelector
// dropdown. The workstation ID is stored in localStorage under
// "wholesale_workstation_id". createDraft(workstationId, "wholesale") passes
// this ID so the backend resolveOrderWarehouse() picks the correct warehouse.

import { useState, useCallback, useEffect, useRef } from "react";
import { Search, Package2, ScanLine, WarehouseIcon } from "lucide-react";
import { usePosCart } from "@/features/pos/use-pos-cart";
import { useWorkstationConfig } from "@/features/pos/use-workstation-config";
import { WorkstationSelector } from "@/features/pos/workstation-selector";
import { CustomerSelect } from "@/features/pos/customer-select";
import { ProductSearchModal } from "@/features/pos/product-search-modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiGet } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { toast } from "@/components/ui/toast";
import { useScanResolve, type ScanContext } from "@/features/inventory/use-scan-resolve";
import { WholesaleLineTable } from "./wholesale-line-table";
import { WholesalePaymentPanel } from "./wholesale-payment-panel";

interface VariantResult {
  id: string;
  sku: string;
  productName: string;
  imageUrls?: string[];
}

// Derive warehouse ID from the committed order by fetching it once.
// The backend assigns warehouse via the workstation; we surface it here
// so the serial selector can filter by warehouse.
function useOrderWarehouseId(orderId: string | undefined) {
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  useEffect(() => {
    if (!orderId) { setWarehouseId(null); return; }
    // The order API returns warehouseId in the order object if available.
    // Fetch order detail to get it.
    apiGet<{ warehouseId?: string | null }>(`/v1/orders/${orderId}`)
      .then((o) => setWarehouseId(o.warehouseId ?? null))
      .catch(() => {/* non-critical — serials will show without warehouse filter */});
  }, [orderId]);
  return warehouseId;
}

export function WholesalePage() {
  const wsConfig = useWorkstationConfig("wholesale");
  const { workstationId, warehouseName, isConfigured } = wsConfig;

  const {
    order,
    addItem,
    updateItem,
    removeItem,
    patchHeader,
    commit,
    createDraft,
    clearCart,
  } = usePosCart();

  const [searchOpen, setSearchOpen] = useState(false);
  const [barcodeValue, setBarcodeValue] = useState("");
  const [busy, setBusy] = useState(false);
  const barcodeRef = useRef<HTMLInputElement>(null);

  // Whether the WorkstationSelector panel is expanded
  const [selectorOpen, setSelectorOpen] = useState(!isConfigured);
  useEffect(() => {
    if (isConfigured) setSelectorOpen(false);
  }, [isConfigured]);

  // Serial-first IMEI scan state
  const [imeiValue, setImeiValue] = useState("");
  const [imeiScanning, setImeiScanning] = useState(false);
  const imeiRef = useRef<HTMLInputElement>(null);

  const warehouseId = useOrderWarehouseId(order?.id);

  // Build scan context: "sell" with the active draft's order ID.
  // Falls back to a disabled-placeholder when no draft exists; the scan box
  // is gated on order?.id so resolve() is never called in that state.
  const imeiScanCtx: ScanContext = order?.id
    ? { context: "sell", orderId: order.id }
    : { context: "sell", orderId: "" };
  const { resolve: resolveImei } = useScanResolve(imeiScanCtx);

  // On mount or when workstation becomes available: ensure a wholesale draft exists.
  const draftInitiated = useRef(false);
  useEffect(() => {
    if (!workstationId || draftInitiated.current || order) return;
    draftInitiated.current = true;
    createDraft(workstationId, "wholesale").catch((err: unknown) => {
      toast.error(translateApiError(err));
      draftInitiated.current = false;
    });
  }, [workstationId, order, createDraft]);

  // Keep focus on barcode box after mutations so adding many lines is fast.
  useEffect(() => {
    if (!addItem.isPending) {
      const t = setTimeout(() => barcodeRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [addItem.isPending]);

  const handleBarcodeKey = useCallback(async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter" || !barcodeValue.trim() || busy) return;
    e.preventDefault();
    setBusy(true);
    try {
      const results = await apiGet<VariantResult[]>("/v1/variants/search", {
        q: barcodeValue.trim(),
        limit: 5,
      });
      setBarcodeValue("");
      if (!results || results.length === 0) {
        toast.error("Không tìm thấy sản phẩm");
        return;
      }
      if (results.length === 1) {
        addItem.mutate(
          { variantId: results[0].id },
          { onError: (err) => toast.error(translateApiError(err)) },
        );
      } else {
        setSearchOpen(true);
      }
    } catch (err) {
      toast.error(translateApiError(err));
    } finally {
      setBusy(false);
      barcodeRef.current?.focus();
    }
  }, [barcodeValue, busy, addItem]);

  const handleSelectVariant = useCallback((variantId: string) => {
    addItem.mutate(
      { variantId },
      { onError: (err) => toast.error(translateApiError(err)) },
    );
    setSearchOpen(false);
    setTimeout(() => barcodeRef.current?.focus(), 80);
  }, [addItem]);

  const handleSelectCustomer = useCallback((c: { id: string } | null) => {
    patchHeader.mutate(
      { customerId: c?.id ?? undefined },
      { onError: (err) => toast.error(translateApiError(err)) },
    );
  }, [patchHeader]);

  const handleUpdateQty = useCallback((itemId: string, qty: number) => {
    updateItem.mutate(
      { itemId, quantity: qty },
      { onError: (err) => toast.error(translateApiError(err)) },
    );
  }, [updateItem]);

  const handleUpdatePrice = useCallback((itemId: string, price: string) => {
    updateItem.mutate(
      { itemId, unitPrice: price },
      { onError: (err) => toast.error(translateApiError(err)) },
    );
  }, [updateItem]);

  const handleUpdateSerials = useCallback((itemId: string, serialIds: string[]) => {
    updateItem.mutate(
      { itemId, serialIds },
      {
        onError: (err) => {
          // Surface backend serial validation errors clearly (wrong variant / already sold / wrong warehouse)
          toast.error(translateApiError(err));
        },
      },
    );
  }, [updateItem]);

  const handleRemove = useCallback((itemId: string) => {
    removeItem.mutate(itemId, {
      onError: (err) => toast.error(translateApiError(err)),
    });
  }, [removeItem]);

  const handleCommit = useCallback(({ paymentMethod, tenderedAmount, reason }: {
    paymentMethod: string;
    tenderedAmount: number;
    reason?: string;
  }) => {
    commit.mutate(
      { paymentMethod, tenderedAmount, reason },
      {
        onError: (err) => toast.error(translateApiError(err)),
      },
    );
  }, [commit]);

  const handleNewOrder = useCallback(() => {
    if (!isConfigured) {
      toast.error("Chưa cấu hình kho bán — liên hệ admin");
      setSelectorOpen(true);
      return;
    }
    clearCart();
    draftInitiated.current = false;
    setBarcodeValue("");
    setImeiValue("");
    // Draft will be created by the useEffect above when workstationId is available
  }, [clearCart, isConfigured]);

  // Serial-first scan: quét IMEI → resolve → tự tìm/tạo dòng + gắn serial.
  // updateItem REPLACES the full serial set — always send the complete merged array.
  const handleImeiKey = useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Enter" || !imeiValue.trim() || imeiScanning || !order?.id) return;
      e.preventDefault();
      const code = imeiValue.trim();

      setImeiScanning(true);
      try {
        const sr = await resolveImei(code);
        setImeiValue("");

        if (sr.kind === "none") {
          toast.error("Không tìm thấy serial hoặc sản phẩm");
          return;
        }

        if (sr.kind === "variant") {
          // Fuzzy variant match — delegate to existing barcode flow
          if (sr.matches.length === 1) {
            addItem.mutate(
              { variantId: sr.matches[0].id },
              { onError: (err) => toast.error(translateApiError(err)) },
            );
          } else {
            setSearchOpen(true);
          }
          return;
        }

        // kind === "serial"
        if (!sr.ok) {
          toast.error(sr.reason ?? "Serial không hợp lệ cho kho bán buôn");
          return;
        }

        const variantId = sr.variant.id;
        const serialId = sr.serial.id;

        // Find existing line for this variant in the current order
        const currentItems = order.items ?? [];
        const existingItem = currentItems.find((it) => it.variantId === variantId);

        if (existingItem) {
          // Line already exists — build full merged serial set and replace
          const existingIds = (existingItem.serials ?? []).map((s) => s.id);

          if (existingIds.includes(serialId)) {
            toast.info("Serial đã có trong đơn hàng");
            return;
          }

          const mergedIds = [...existingIds, serialId];
          updateItem.mutate(
            { itemId: existingItem.id, serialIds: mergedIds, quantity: mergedIds.length },
            { onError: (err) => toast.error(translateApiError(err)) },
          );
        } else {
          // No line yet — addItem first, then attach the serial via updateItem.
          // addItem returns the full updated order; we find the newly created item
          // from the response to get its ID for the subsequent updateItem call.
          addItem.mutate(
            { variantId },
            {
              onSuccess: (updatedOrder) => {
                const newItem = (updatedOrder.items ?? []).find(
                  (it) => it.variantId === variantId,
                );
                if (!newItem) {
                  toast.error("Không thể gắn serial: dòng vừa tạo không tìm thấy");
                  return;
                }
                // New item has no serials yet; send the single serial as the full set
                updateItem.mutate(
                  { itemId: newItem.id, serialIds: [serialId], quantity: 1 },
                  { onError: (err) => toast.error(translateApiError(err)) },
                );
              },
              onError: (err) => toast.error(translateApiError(err)),
            },
          );
        }
      } finally {
        setImeiScanning(false);
        setTimeout(() => imeiRef.current?.focus(), 60);
      }
    },
    [imeiValue, imeiScanning, order, resolveImei, addItem, updateItem],
  );

  const mutationLoading =
    addItem.isPending || updateItem.isPending || removeItem.isPending;

  const items = order?.items ?? [];

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col overflow-hidden">
      {/* Header bar */}
      <div className="flex items-center gap-3 border-b bg-card px-4 py-2 shrink-0 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Package2 size={18} className="text-primary shrink-0" />
          <span className="font-semibold text-sm">Bán sỉ</span>
          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-bold text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
            BÁN SỈ
          </span>
          {order && (
            <span className="text-xs text-muted-foreground font-mono">{order.code}</span>
          )}
        </div>

        {/* Warehouse badge */}
        {isConfigured ? (
          <button
            type="button"
            onClick={() => setSelectorOpen((v) => !v)}
            className="flex items-center gap-1.5 rounded-md bg-green-50 border border-green-200 px-2 py-1 text-xs text-green-700 hover:bg-green-100 transition-colors dark:bg-green-950/30 dark:border-green-800 dark:text-green-300"
            title="Nhấn để đổi cấu hình kho bán"
          >
            <WarehouseIcon size={12} className="shrink-0" />
            <span className="font-medium">Đang bán từ: {warehouseName ?? workstationId}</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setSelectorOpen(true)}
            className="flex items-center gap-1.5 rounded-md bg-amber-50 border border-amber-300 px-2 py-1 text-xs text-amber-700 hover:bg-amber-100 transition-colors dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-400"
          >
            <WarehouseIcon size={12} className="shrink-0" />
            <span className="font-medium">Chưa cấu hình kho bán — nhấn để cấu hình</span>
          </button>
        )}

        {/* Customer selector */}
        <CustomerSelect
          selectedId={order?.customerId}
          onSelect={handleSelectCustomer}
          mode="chip"
        />

        {/* Barcode / SKU quick-add input — stays focused for bulk entry */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            ref={barcodeRef}
            value={barcodeValue}
            onChange={(e) => setBarcodeValue(e.target.value)}
            onKeyDown={handleBarcodeKey}
            placeholder="Barcode / SKU, Enter để thêm..."
            disabled={!workstationId || busy || addItem.isPending}
            className="pl-8 font-mono text-sm h-9"
            autoComplete="off"
          />
        </div>

        {/* IMEI / serial scan box — serial-first: quét IMEI → tự thêm dòng + gắn serial */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <ScanLine size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            ref={imeiRef}
            value={imeiValue}
            onChange={(e) => setImeiValue(e.target.value)}
            onKeyDown={handleImeiKey}
            placeholder={
              order?.id
                ? "Quét IMEI / Serial..."
                : "Cần có đơn hàng để quét serial"
            }
            disabled={!order?.id || imeiScanning || addItem.isPending || updateItem.isPending}
            className="pl-8 font-mono text-sm h-9"
            autoComplete="off"
          />
          {imeiScanning && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
              …
            </span>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => setSearchOpen(true)}
          disabled={!workstationId}
          className="gap-1.5 shrink-0"
        >
          <Search size={14} />
          Tìm sản phẩm
        </Button>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: workstation setup or line table */}
        <div className="flex flex-1 flex-col overflow-auto">
          {/* Inline WorkstationSelector panel when not configured or manually opened */}
          {(!isConfigured || selectorOpen) && (
            <div className="p-4 max-w-sm">
              <WorkstationSelector
                channel="wholesale"
                onConfigured={() => setSelectorOpen(false)}
              />
            </div>
          )}

          {/* Line table — always shown when configured */}
          {isConfigured && (
            <WholesaleLineTable
              items={items}
              warehouseId={warehouseId}
              loading={mutationLoading}
              onUpdateQty={handleUpdateQty}
              onUpdatePrice={handleUpdatePrice}
              onUpdateSerials={handleUpdateSerials}
              onRemove={handleRemove}
            />
          )}
        </div>

        {/* Right: payment panel */}
        <div className="hidden lg:flex w-[320px] shrink-0 border-l bg-card flex-col overflow-y-auto">
          <WholesalePaymentPanel
            order={order}
            workstationId={workstationId}
            loading={commit.isPending}
            onCommit={handleCommit}
            onNewOrder={handleNewOrder}
          />
        </div>
      </div>

      {/* Mobile payment button — visible on small screens */}
      <div className="lg:hidden border-t bg-card px-4 py-2 flex items-center justify-between shrink-0">
        <div className="text-sm">
          {order?.total ? (
            <span className="font-semibold font-mono">
              {new Intl.NumberFormat("vi-VN").format(parseFloat(order.total))} ₫
            </span>
          ) : (
            <span className="text-muted-foreground">Chưa có đơn</span>
          )}
        </div>
        {order?.status === "paid" ? (
          <Button size="sm" onClick={handleNewOrder}>Đơn mới</Button>
        ) : (
          <Button
            size="sm"
            disabled={!order?.items?.length || commit.isPending}
            onClick={() => {
              // On mobile, the full panel scrolls into view; minimal UX here.
              toast.info("Kéo qua phải để thanh toán trên màn hình lớn hơn.");
            }}
          >
            Thanh toán
          </Button>
        )}
      </div>

      <ProductSearchModal
        open={searchOpen}
        onSelect={handleSelectVariant}
        onClose={() => setSearchOpen(false)}
      />
    </div>
  );
}
